import {
  WSAlert,
  WSChip,
  WSElement,
  WSEmptyState,
  WSFlexBox,
  WSInfiniteScroll,
  WSList,
  WSPage,
  WSPageToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  ListFormsSortParameterValue,
  PayerStatus
} from "@wingspanhq/wingspan-api-sdk";
import { Route, Switch, useHistory } from "react-router-dom";
import { queriesPayerRows, queriesRequirements } from "@query";
import { useAddClientModal } from "../components/AddClientModal/useAddClientModal";
import { TablePayers } from "../components/TablePayers/TablePayers";

import { useCallback } from "react";
import { Tabs } from "../../../components/Tabs";
import { KEYBOARD_SHORTCUT_SEARCH } from "../../../constants/keyboardShortcuts";
import { useSession } from "@hooks/useSession";
import pluralize from "pluralize";
import { useOpenModalBySearchParam } from "../../../utils/useOpenModalBySearchParam";
import { useRedirectToByKeyPress } from "../../../utils/useRedirectToByKeyPress";
import { useCustomization } from "../../customization";
import { selectorExpiredRequirements } from "../../Requirements/selectors/selectorExpiredRequirements";
import { selectorExpiringRequirements } from "../../Requirements/selectors/selectorExpiringRequirements";
import { selectorIsAllRequirementsComplete } from "../../Requirements/selectors/selectorIsAllRequirementsComplete";
import { selectorIsRequirementArchived } from "../../Requirements/selectors/selectorIsRequirementArchived";
import { PAYERS_PATH, PAYERS_REQUIREMENTS_PATH } from "../paths";
import { usePayerRequirements } from "../../Requirements/modules/custom/payer/usePayerRequirements";
import { RouteClientsRequirements } from "./RouteClientsRequirements";
import styles from "./styles.module.scss";

export const RouteClients: React.FC = () => {
  const { terminology } = useCustomization();
  const history = useHistory();
  const { accountId } = useSession();
  const addClientModal = useAddClientModal();
  useOpenModalBySearchParam("add_client", addClientModal);

  // v3 clients list — active clients off the denormalized search rows
  // (searchPayerRows), the twin of the v1 search-service /payer-row. Server-side
  // status filter + cursor pagination (infinite scroll below). Archived clients
  // (status Deactivated) have their own route; free-text search is a separate route.
  // Filter and sort mirror v1: `payeeOwnedData.status = Active`, newest-updated first.
  const queryPayers = queriesPayerRows.searchPayerRows.usePaginatedQuery({
    filter: { status: { eq: PayerStatus.ACTIVATED } },
    sort: { updatedAt: ListFormsSortParameterValue.DESC }
  });
  const payers = queryPayers.flatData ?? [];
  const totalCount =
    queryPayers.data?.pages?.[0]?.pagination?.totalSize ?? payers.length;

  // TODO(v3-blocker): v1 also sorted `updatedAt desc`; v3 `listPayeeRequirements`
  // has no sort param and the instance carries no `updatedAt` (only `createdAt` +
  // lifecycle stamps), so incoming order is the server's. Harmless here:
  // `usePayerRequirements` re-sorts by priority before anything renders.
  const queryRequirements =
    queriesRequirements.listPayeeRequirements.useQueryAll(
      {
        filter: {
          payeeAccountId: { in: [accountId] }
        }
      },
      // Never send the filter without an id: `{ in: [undefined] }` serializes to
      // `filter[payeeAccountId][in]=null`, which the API rejects with a 422.
      { enabled: !!accountId }
    );

  const onAddClient = useCallback(async () => {
    // Straight to the new client's details, as the contractor flow does. The
    // clients list is the denormalized search projection, which takes a while to
    // materialize a brand-new client, so landing on the detail screen (read
    // directly by id) is also the only immediate confirmation available.
    await addClientModal.open({ redirectToDetails: true });
  }, [addClientModal]);

  useRedirectToByKeyPress(KEYBOARD_SHORTCUT_SEARCH, `${PAYERS_PATH}/search`);

  // v1 narrowed the list to engagement-attached requirements
  // (`payerPayeeEngagementIds != null`). v3 has no existence operator, so the one
  // read is narrowed client-side on `selectorIsRequirementArchived` — the
  // codebase's canonical "not attached to any engagement" rule, and what the
  // client detail screen already does. Without it, requirements v1 hid would
  // raise the expiring/expired alerts and surface the Requirements tab.
  const engagementRequirements = (queryRequirements.flatData || []).filter(
    requirement => !selectorIsRequirementArchived(requirement)
  );

  const requirements = usePayerRequirements(engagementRequirements);

  const isRequirementsComplete =
    selectorIsAllRequirementsComplete(requirements);

  const expiringRequirements = selectorExpiringRequirements(
    engagementRequirements
  );

  const expiredRequirements = selectorExpiredRequirements(
    engagementRequirements
  );

  return (
    <WSPage>
      <WSPageToolbar
        title={pluralize.plural(terminology().getPaidClient)}
        actions={[
          {
            label: "Search",
            icon: "search",
            buttonKind: "Tertiary",
            hideButtonOn: false,
            buttonOrder: 0,
            onClick: () => history.push(`${PAYERS_PATH}/search`)
          },
          {
            label: "Add client",
            icon: "plus",
            buttonKind: "Primary",
            onClick: onAddClient
          },
          {
            icon: "archive",
            label: "View archived clients",
            onClick: () => {
              history.push(`${PAYERS_PATH}/archived`);
            }
          }
        ]}
      />
      {(expiringRequirements.length > 0 || expiredRequirements.length > 0) && (
        <WSList mb="L">
          {expiredRequirements.length > 0 && (
            <WSAlert
              theme="warning"
              icon="alert"
              title="Requirements incomplete"
              action={{
                text: "View requirements",
                onClick: () => {
                  history.push(PAYERS_REQUIREMENTS_PATH);
                }
              }}
            >
              You must complete all eligibility requirements for an engagement
              before you can receive payments for work under that engagement.
            </WSAlert>
          )}

          {expiringRequirements.length > 0 && (
            <WSAlert
              theme="neutral"
              icon="calendar"
              title="Requirements expiring soon"
              action={{
                text: "View requirements",
                onClick: () => {
                  history.push(PAYERS_REQUIREMENTS_PATH);
                }
              }}
            >
              You have {expiringRequirements.length} requirements expiring soon.
            </WSAlert>
          )}
        </WSList>
      )}

      {requirements.length ? (
        <Tabs
          mb="L"
          tabs={[
            {
              label: "Clients",
              path: PAYERS_PATH
            },
            {
              label: "Requirements",
              path: PAYERS_REQUIREMENTS_PATH,
              pill: {
                icon: true,
                theme: isRequirementsComplete ? "success" : "warning"
              }
            }
          ]}
        />
      ) : null}
      <Switch>
        <Route
          path={PAYERS_PATH}
          exact
          render={() => (
            <>
              <WSFlexBox.CenterY mb="L" className={styles.statsContainer}>
                <WSElement className={styles.resultsStats}>
                  <WSText.ParagraphXs color="gray500">
                    Showing:
                  </WSText.ParagraphXs>
                  <WSChip
                    pillText={`${totalCount} item${
                      totalCount === 1 ? "" : "s"
                    }`}
                    theme="neutral"
                    pillType="noBackground"
                  />
                </WSElement>
              </WSFlexBox.CenterY>
              {/* v1 wrapped the list in WSQueries, which rendered a loader
                  first and a failed read as an error rather than as an empty
                  list. Same branches here, in the same order as the contractors
                  list: loading → error → empty → table. Loading comes FIRST so
                  the first paint is the table skeleton — ordering it after the
                  empty check leaves a window where neither renders and the area
                  is blank. */}
              {queryPayers.isLoading ? (
                <TablePayers payers={[]} loading />
              ) : queryPayers.isError ? (
                <WSEmptyState
                  hasBorder
                  bgColor="gray"
                  type="error"
                  title="Couldn’t load clients"
                  description="Something went wrong while loading clients."
                  actions={{
                    buttons: [
                      {
                        label: "Try again",
                        kind: "Secondary",
                        onClick: () => queryPayers.refetch()
                      }
                    ]
                  }}
                />
              ) : payers.length === 0 ? (
                <WSEmptyState
                  hasBorder
                  bgColor="gray"
                  type="people"
                  title="No clients yet"
                  description="Once a client is added, they will appear here."
                  actions={{
                    buttons: [
                      {
                        label: "Add client",
                        kind: "Secondary",
                        onClick: onAddClient
                      }
                    ]
                  }}
                />
              ) : (
                <WSInfiniteScroll
                  onLoad={() => queryPayers.fetchNextPage()}
                  loadMore={payers.length > 0}
                  endOfList={!queryPayers.hasNextPage}
                  loading={!!queryPayers.isFetchingNextPage}
                >
                  <TablePayers payers={payers} />
                </WSInfiniteScroll>
              )}
            </>
          )}
        />
        <Route
          render={() => (
            <RouteClientsRequirements
              isLoading={queryRequirements.isFetching}
              requirements={requirements}
            />
          )}
          path={PAYERS_REQUIREMENTS_PATH}
          exact
        />
      </Switch>
    </WSPage>
  );
};
